import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui';
import { CategorySearchSelect } from './CategorySearchSelect';
import { categoryDisplayName, useAdminCategoryItemsQuery, type CategoryItem } from '../hooks/useCategoriesQuery';

/**
 * QA-166 — 상품 등록/수정 다중 카테고리 + 대표 카테고리.
 *
 * 기존 단일 `primaryCategoryId` 셀렉터를 다중 관리 UI 로 확장한다(재구축이 아니라 갭 보완):
 *   - 선택된 카테고리 목록 = [대표(primaryCategoryId), ...추가(additionalCategoryIds)].
 *   - [+] 로 카테고리 추가(이미 선택된 건 중복 차단). 첫 추가는 자동으로 대표가 된다.
 *   - 각 행의 라디오로 대표 카테고리 변경(default=현재 primary). 대표 변경 시 기존 대표는 추가로 강등.
 *   - 행 삭제. 대표를 삭제하면 남은 첫 카테고리가 대표로 승격(없으면 대표 비움).
 *
 * 계약(불변): BE product create/update 는 `primaryCategoryId`(=대표) + `additionalCategoryIds[]`
 * 를 그대로 수용·영속하고, 응답 commerceMeta.additionalCategoryIds 로 재시드된다(detailMapper).
 * 본 컴포넌트는 그 두 값을 읽고/쓰기만 한다 — 와이어 계약 변경 없음. 내부 식별자(categoryId)는
 * 화면에 노출하지 않고 카테고리 경로(대>중>소 이름)만 표시한다.
 *
 * 상위 카테고리 노출 — 쇼핑몰 카테고리 목록은 직접 연결된 상품만 보여 준다(하위 카테고리 상품이 상위
 * 목록에 저절로 나오지 않는다, 고도몰 `cateLinkFl = 'y'` 와 같은 동작). 고도몰은 하위를 고르면 상위를
 * 함께 보여 주고 운영자가 "상위 노출" 을 체크했으므로, 선택한 카테고리의 조상을 체크박스로 보여 준다.
 * 체크 = 그 조상을 additionalCategoryIds 에 넣기, 해제 = 빼기. 기본은 체크 해제(고도몰 기본값 n).
 * 조상도 추가 카테고리 상한(10개)에 포함된다.
 */

export interface ProductCategoriesFieldProps {
  primaryCategoryId: string | null;
  additionalCategoryIds: ReadonlyArray<string>;
  disabled?: boolean;
  errorMessage?: string | null;
  /** 최대 추가 카테고리 수(대표 제외). 스키마 additionalCategoryIds.max(10) 와 정합. */
  maxAdditional?: number;
  onChange: (next: { primaryCategoryId: string | null; additionalCategoryIds: string[] }) => void;
}

const buildPath = (
  items: ReadonlyArray<CategoryItem>,
  categoryId: string,
): string => {
  const byId = new Map(items.map((c) => [c.categoryId, c]));
  const walk = (id: string | null, acc: ReadonlyArray<string>): ReadonlyArray<string> => {
    if (!id) return acc;
    const node = byId.get(id);
    if (!node) return acc;
    return walk(node.parentCategoryId, [categoryDisplayName(node), ...acc]);
  };
  const path = walk(categoryId, []);
  // 미해석(목록에 없는 id)은 경로를 못 만들므로 빈 문자열 — 호출부에서 폴백 처리.
  return path.join(' > ');
};

/**
 * 선택한 카테고리들의 조상 id 목록(가까운 조상부터, 중복 제거). 대표 카테고리는 이미 직접 연결이라
 * 체크 대상이 아니므로 뺀다. 이미 추가 카테고리로 고른 조상은 남겨 두어 체크된 상태로 보인다.
 */
const collectAncestorIds = (
  items: ReadonlyArray<CategoryItem>,
  selectedIds: ReadonlyArray<string>,
  primaryCategoryId: string | null,
): string[] => {
  const byId = new Map(items.map((c) => [c.categoryId, c]));
  const out: string[] = [];
  selectedIds.forEach((id) => {
    const seen = new Set<string>([id]);
    let parent = byId.get(id)?.parentCategoryId ?? null;
    while (parent && !seen.has(parent)) {
      seen.add(parent);
      if (parent !== primaryCategoryId && !out.includes(parent)) out.push(parent);
      parent = byId.get(parent)?.parentCategoryId ?? null;
    }
  });
  return out;
};

export const ProductCategoriesField = ({
  primaryCategoryId,
  additionalCategoryIds,
  disabled,
  errorMessage,
  maxAdditional = 10,
  onChange,
}: ProductCategoriesFieldProps) => {
  // 숨김 카테고리 이름까지 보이게 관리자 목록(AUDITOR 403 이면 공개 목록으로 대신).
  const query = useAdminCategoryItemsQuery();
  const items = useMemo(() => query.data ?? [], [query.data]);

  // 추가 셀렉터를 선택 후 초기화하기 위한 remount 키.
  const [addKey, setAddKey] = useState(0);

  // 선택 순서 보존: 대표 먼저, 그다음 추가들(중복 제거).
  const selectedIds = useMemo(() => {
    const ordered: string[] = [];
    if (primaryCategoryId) ordered.push(primaryCategoryId);
    additionalCategoryIds.forEach((id) => {
      if (id && id !== primaryCategoryId && !ordered.includes(id)) ordered.push(id);
    });
    return ordered;
  }, [primaryCategoryId, additionalCategoryIds]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // 대표를 제외한 나머지를 additionalCategoryIds 로 분리해 emit.
  const emit = (primary: string | null, all: ReadonlyArray<string>) => {
    const additional = all.filter((id) => id !== primary);
    onChange({ primaryCategoryId: primary, additionalCategoryIds: additional });
  };

  const handleAdd = (categoryId: string | null) => {
    setAddKey((k) => k + 1); // 셀렉터 초기화
    if (!categoryId || selectedSet.has(categoryId)) return;
    // 첫 카테고리는 자동 대표. 이후는 추가 목록 말미에.
    if (selectedIds.length === 0) {
      emit(categoryId, [categoryId]);
      return;
    }
    if (additionalCategoryIds.filter((id) => id !== primaryCategoryId).length >= maxAdditional) {
      return; // 상한 초과는 무시(상한 안내는 errorMessage/스키마가 담당).
    }
    emit(primaryCategoryId, [...selectedIds, categoryId]);
  };

  const handleRemove = (categoryId: string) => {
    const next = selectedIds.filter((id) => id !== categoryId);
    if (categoryId === primaryCategoryId) {
      // 대표 삭제 → 남은 첫 카테고리 승격(없으면 비움).
      const nextPrimary = next[0] ?? null;
      emit(nextPrimary, next);
    } else {
      emit(primaryCategoryId, next);
    }
  };

  const handleSetPrimary = (categoryId: string) => {
    if (categoryId === primaryCategoryId) return;
    emit(categoryId, selectedIds);
  };

  const additionalCount = selectedIds.filter((id) => id !== primaryCategoryId).length;
  const atLimit = additionalCount >= maxAdditional;

  const ancestorIds = useMemo(
    () => collectAncestorIds(items, selectedIds, primaryCategoryId),
    [items, selectedIds, primaryCategoryId],
  );

  const handleToggleAncestor = (categoryId: string, checked: boolean) => {
    if (!checked) {
      emit(primaryCategoryId, selectedIds.filter((id) => id !== categoryId));
      return;
    }
    if (selectedSet.has(categoryId) || atLimit) return;
    emit(primaryCategoryId, [...selectedIds, categoryId]);
  };

  return (
    <div className="flex flex-col gap-2" data-testid="product-categories-field">
      {selectedIds.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {selectedIds.map((categoryId) => {
            const path = buildPath(items, categoryId);
            const isPrimary = categoryId === primaryCategoryId;
            return (
              <li
                key={categoryId}
                className="flex items-center gap-3 rounded-md px-3 py-2"
                style={{
                  background: 'var(--surface-1)',
                  border: '1px solid var(--divider-strong)',
                }}
              >
                <label className="flex shrink-0 items-center gap-1.5 text-xs" style={{ color: 'var(--content-default)' }}>
                  <input
                    type="radio"
                    name="product-representative-category"
                    checked={isPrimary}
                    disabled={disabled}
                    onChange={() => handleSetPrimary(categoryId)}
                    aria-label={`${path || '카테고리'} 대표 설정`}
                  />
                  대표
                </label>
                <span
                  className="min-w-0 flex-1 truncate text-sm"
                  style={{ color: 'var(--content-strong)' }}
                  title={path || undefined}
                >
                  {path || (query.isPending ? '카테고리 불러오는 중…' : '(이름 없는 카테고리)')}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() => handleRemove(categoryId)}
                  aria-label={`${path || '카테고리'} 삭제`}
                >
                  <Trash2 size={14} style={{ color: 'var(--signal-danger)' }} />
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {ancestorIds.length > 0 ? (
        <fieldset
          className="flex flex-col gap-1.5 rounded-md px-3 py-2"
          style={{ border: '1px dashed var(--divider-strong)' }}
          data-testid="product-category-ancestors"
        >
          <legend className="px-1 text-xs" style={{ color: 'var(--content-default)' }}>
            상위 카테고리에도 노출
          </legend>
          <p className="text-xs" style={{ color: 'var(--content-muted)' }}>
            쇼핑몰 카테고리 목록에는 직접 연결된 상품만 나옵니다. 상위 카테고리 목록에도 보이게 하려면
            체크하세요. 체크한 카테고리는 추가 카테고리(최대 {maxAdditional}개)에 포함됩니다.
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {ancestorIds.map((categoryId) => {
              const path = buildPath(items, categoryId);
              const checked = selectedSet.has(categoryId);
              return (
                <label
                  key={categoryId}
                  className="flex items-center gap-1.5 text-sm"
                  style={{ color: 'var(--content-strong)' }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled || (!checked && atLimit)}
                    onChange={(e) => handleToggleAncestor(categoryId, e.target.checked)}
                    aria-label={`${path || '카테고리'} 에도 노출`}
                  />
                  {path || '(이름 없는 카테고리)'}
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      {/* [+] 카테고리 추가 — 기존 트리 셀렉터 재사용. 선택 후 즉시 목록에 합류하고 셀렉터는 초기화. */}
      {atLimit ? (
        <p className="text-xs" style={{ color: 'var(--content-muted)' }}>
          추가 카테고리는 최대 {maxAdditional}개까지 등록할 수 있습니다.
        </p>
      ) : (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <CategorySearchSelect
              key={addKey}
              value={null}
              {...(disabled !== undefined ? { disabled } : {})}
              onChange={handleAdd}
              placeholder={selectedIds.length > 0 ? '카테고리 추가' : '카테고리를 선택하세요'}
              testId="product-category-add"
              includeHidden
            />
          </div>
          <span className="shrink-0 text-xs" style={{ color: 'var(--content-muted)' }}>
            <Plus size={12} aria-hidden="true" className="mr-0.5 inline" />
            다중 등록 가능
          </span>
        </div>
      )}

      {errorMessage ? (
        <span className="text-xs" style={{ color: 'var(--signal-danger)' }}>
          {errorMessage}
        </span>
      ) : null}
    </div>
  );
};
